import { InstrumentationBase, InstrumentationConfig } from '@opentelemetry/instrumentation';
import { context, diag, Span, trace } from '@opentelemetry/api';

import { getTraceContext, tracer } from '../utils/traces';
import { LoadEvent } from '../types/event';
import { useEventStore } from '@/telemetry-sdk/store/event';

const otelLogger = diag.createComponentLogger({
  namespace: '✨ @telemetry-sdk/instrumentation/load',
});

interface CustomLoadInstrumentationConfig extends InstrumentationConfig {
  hooks: {
    onLoadEvent: (loadEvent: LoadEvent, traceId?: string) => void;
  };
}

class CustomLoadInstrumentation extends InstrumentationBase<CustomLoadInstrumentationConfig> {
  private rootSpan?: Span;
  private renderTimer?: any;

  constructor(config: CustomLoadInstrumentationConfig) {
    super('CustomLoadInstrumentation', '1.0.0', config);
  }

  // helpers
  private _getConfig(): CustomLoadInstrumentationConfig {
    return this.getConfig() as CustomLoadInstrumentationConfig;
  }

  private _createActiveRootSpan = (name: string, startTime: number) => {
    const rootSpan = tracer().startSpan(name, { startTime }, getTraceContext(window.traceCarrier));
    window.rootContext = trace.setSpan(context.active(), rootSpan);
    return rootSpan;
  };

  // handlers
  private handleInitial(): void {
    try {
      const eventType = 'initial';
      const readyEvent: LoadEvent = {
        type: eventType,
        timeStamp: 0,
      };
      if (!this.rootSpan) {
        this.rootSpan = this._createActiveRootSpan('Page Render', readyEvent.timeStamp);
        this.rootSpan.addEvent(eventType, readyEvent.timeStamp);
      }
      this._getConfig().hooks?.onLoadEvent?.(readyEvent, window?.traceCarrier?.traceparent);
    } catch (error) {
      otelLogger.error('Error in instrumentation ready handler:', error);
    }
  }

  private handleRendered(): void {
    try {
      const { pageKey, pageLoadedSelectorsMap } = useEventStore.getState();
      const selectorsToWatch = pageLoadedSelectorsMap[pageKey] ?? [];
      const selectorsMissingInDom = selectorsToWatch.some((selector) => {
        return !document.querySelector(selector);
      });

      if (selectorsMissingInDom) return;
      clearInterval(this.renderTimer); // stop the polling

      const renderedEvent: LoadEvent = {
        type: 'rendered',
        timeStamp: performance.now(),
      };

      this.rootSpan?.addEvent('rendered', renderedEvent.timeStamp);
      this.rootSpan?.end(renderedEvent.timeStamp);
      this.rootSpan = undefined;

      this._getConfig().hooks?.onLoadEvent?.(renderedEvent, window?.traceCarrier?.traceparent);
    } catch (error) {
      otelLogger.error('Error in instrumentation Rendered handler:', error);
    }
  }

  // lifecycle
  protected init(): void {}

  override enable(): void {
    try {
      // Cleanup old listeners
      this.disable();

      // If document js is ready, create initial event
      if (window.document.readyState === 'complete') {
        this.handleInitial();
      } else {
        this.handleInitial = this.handleInitial.bind(this);
        window.addEventListener('load', this.handleInitial);
        window.addEventListener('unload', this.disable);
      }

      this.renderTimer = setInterval(() => {
        this.handleRendered();
      }, 300);
    } catch (error) {
      otelLogger.error('Error in instrumentation enable:', error);
    }
  }

  override disable(): void {
    try {
      window.removeEventListener('load', this.handleInitial);
      window.removeEventListener('unload', this.disable);

      clearInterval(this.renderTimer);
    } catch (error) {
      otelLogger.error('Error in instrumentation disable:', error);
    }
  }
}

export { CustomLoadInstrumentation };
